/**
 * Clipboard helpers shared by the task result views.
 *
 * `navigator.clipboard` is only defined in a secure context (https or localhost).
 * The dev server runs with `host: true`, so reaching the app over a LAN IP serves
 * it as plain HTTP and the async clipboard API disappears entirely — hence the
 * execCommand tier, which has no such restriction.
 */

/** Last-resort copy for browsers without an async clipboard. */
function legacyCopy(text: string): boolean {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(ta);
  }
}

/**
 * Copy text to the clipboard, optionally with a rich `text/html` flavor.
 * Resolves to whether the copy actually landed — callers must not report
 * success without checking.
 */
export async function copyToClipboard(text: string, html?: string): Promise<boolean> {
  if (html && navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        }),
      ]);
      return true;
    } catch {
      // Rich copy unsupported or rejected — fall back to plain text.
    }
  }

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall back to execCommand.
    }
  }

  return legacyCopy(text);
}

/**
 * Copy and fire the matching toast. The common case for task result views.
 */
export async function copyWithToast(
  text: string,
  showToast?: (message: string) => void,
  successMessage = 'Copied to clipboard!',
  failureMessage = 'Failed to copy to clipboard',
): Promise<boolean> {
  const copied = await copyToClipboard(text);
  showToast?.(copied ? successMessage : failureMessage);
  return copied;
}
